<script setup>
const props = defineProps({
    show: Boolean
});

const emit = defineEmits(['close', 'select-cash', 'select-card']);

function selectCash() {
    emit('select-cash');
}

function selectCard() {
    emit('select-card');
}

function close() {
    emit('close');
}
</script>

<template>
    <Transition name="modal">
        <div v-if="show" class="modal-overlay" @click.self="close">
            <div class="modal-container">
                <header class="modal-header">
                    <h2 class="modal-title">Método de pago</h2>
                    <button class="close-btn" @click="close" aria-label="Cerrar">
                        <span class="material-symbols-rounded">close</span>
                    </button>
                </header>

                <div class="modal-body">
                    <p class="modal-subtitle">Selecciona una opción</p>

                    <div class="payment-options">
                        <button class="option-btn cash" @click="selectCash">
                            <div class="icon-wrapper">
                                <span class="material-symbols-rounded">payments</span>
                            </div>
                            <span class="option-label">Efectivo</span>
                        </button>

                        <button class="option-btn card" @click="selectCard">
                            <div class="icon-wrapper">
                                <span class="material-symbols-rounded">credit_card</span>
                            </div>
                            <span class="option-label">Tarjeta</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 420px;
    overflow: hidden;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 1.5rem 1.75rem 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
    text-align: center;
}

.close-btn {
    position: absolute;
    right: 1rem;
    top: 1rem;
    background: transparent;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    background: #f3f4f6;
    color: #1f2937;
}

.modal-body {
    padding: 0 2rem 2.5rem;
    text-align: center;
}

.modal-subtitle {
    color: #6b7280;
    font-size: 1rem;
    margin-bottom: 2rem;
}

.payment-options {
    display: flex;
    gap: 1rem;
    justify-content: center;
}

.option-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 1.5rem 1rem;
    border: none;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    background: #f3f4f6;
    color: #374151;
}

.option-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.option-btn:active {
    transform: translateY(-2px);
}

.icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
}

.option-btn .material-symbols-rounded {
    font-size: 32px;
}

.option-label {
    font-weight: 600;
    font-size: 1.1rem;
}

/* Cash Button Styles */
.option-btn.cash {
    background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
    color: white;
}

.option-btn.cash .icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.option-btn.cash:hover {
    box-shadow: 0 12px 24px rgba(99, 102, 241, 0.3);
}

/* Card Button Styles */
.option-btn.card {
    background: linear-gradient(135deg, #f43f5e 0%, #e11d48 100%);
    color: white;
}

.option-btn.card .icon-wrapper {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.option-btn.card:hover {
    box-shadow: 0 12px 24px rgba(225, 29, 72, 0.3);
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.95);
    opacity: 0;
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-subtitle {
        color: #94a3b8;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover {
        background: #334155;
        color: #e2e8f0;
    }
}

:root[data-theme="dark"] {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-subtitle {
        color: #94a3b8;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover {
        background: #334155;
        color: #e2e8f0;
    }
}
</style>
